// OnboardingStep5.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import {
  useAuth,
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useDarkMode } from "@/hooks/useDarkMode";
import WideButton from "@/components/common/WideButton";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";

interface OnboardingStep5Props {
  onClose: () => void;
  title: string;
  subtitle: string;
  onSkip: () => void;
}

const OnboardingStep5 = ({
  onClose,
  title,
  subtitle,
}: OnboardingStep5Props) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const { user } = useAuth();
  const isDark = useDarkMode();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();
  const { modalStyle, modalClassName } = useModalHeight();
  //fetch data options to display
  const { howDoYouHearAboutOptions } = useAccessibility();

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [other, setOther] = useState("");

  useEffect(() => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountHowDidYouFindOutAboutSociabilityPage",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountHowDidYouFindOutAboutSociabilityPage",
      {},
    );
  }, []);

  const handleOptionChange = (option: number) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountHowDidYouFindOutAboutSociabilityPageClickedContinue",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountHowDidYouFindOutAboutSociabilityPageClickedContinue",
      {},
    );

    AnalyticsService.trackGAEventHandler("authPopupCreateAccountUpdateProfile");
    AnalyticsService.trackMx("authPopupCreateAccountUpdateProfile", {});

    setSubmitData((prev) => ({
      ...prev,
      how_do_you_hear_about_us_ids: selectedOptions.join(","),
    }));

    if (!user) {
      //move to next step
      setScreenIndex(AuthModalScreen.VerifyEmail);
    } else {
      onClose();
    }
  };

  console.log("submitData from OnboardingStep4", submitData);
  const filteredOptions = howDoYouHearAboutOptions.filter(
    (e) => e.title !== "Other: Please specify",
  );

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={() => {
            setScreenIndex(AuthModalScreen.OnboardingStep4);
          }}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="flex-1 px-8 md:px-16 overflow-y-scroll">
        <div className="mt-6">
          <h2 className="mb-4 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong">{subtitle}</p>
          <div>
            <hr className="w-full border-border-token-brand-minimal my-4" />
          </div>
        </div>
        <div className="title-s-semibold">
          {i18n.get("howDidYouFindOutAboutSociability")}
        </div>
        <div className="mt-2 mb-3 body-l-semibold !text-fg-brand-subtle">
          {i18n.get("selectAllThatApply")}
        </div>
        <div className="flex flex-wrap gap-3 mb-8 mt-4">
          {filteredOptions.map((option, index) => {
            // const isSelected = selectedOptions.includes(option.id);
            return (
              <div
                key={index}
                onClick={() => handleOptionChange(option.id)}
                role="button"
                className={`flex items-center gap-1 px-4 py-2.5 rounded-3xl ${selectedOptions.includes(option.id) ? "bg-orange-300" : "bg-violet-100"}`}
                tabIndex={0}
              >
                <p className="text-fg-neutral-strong label-m-medium">
                  {option.title}
                </p>
              </div>
            );
          })}
          <div className="w-full">
            <FloatingLabelInput
              label="Other"
              type="text"
              value={other}
              placeholder="Other"
              onChange={(event) => {
                setOther(event.target.value);
              }}
              onClear={() => {
                setOther("");
              }}
            />
          </div>
        </div>
        <div className="mb-8 items-center justify-center flex flex-col pt-2">
          <WideButton
            disabled={selectedOptions.length <= 0 && other.length <= 0}
            type="filled"
            onClick={() => onContinue()}
          >
            {i18n.get("continue")}
          </WideButton>
          <StandardButton
            onClick={onContinue}
            variant="link"
            className="body-l-semibold underline text-links mt-6"
          >
            {i18n.get("skipForNow")}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep5;
